import { createProgressBar } from "../form_subcomponents/progress-bar/progress-bar";

const MULTI_STEP_SELECTORS = {
  container: ".bux-multi-form",
  formContainer: ".bux-multi-form__form-container",
  header: ".bux-multi-form__header",
  forms: ".bux-multi-form__form-step",
  stepTitle: ".bux-multi-form__form-step-title",
  progressBar: ".bux-progress-bar",
  buttonContainer: ".bux-multi-form__button-container",
  nextButton: ".bux-multi-form__next-button button",
  backButton: ".bux-multi-form__back-button button",
  confirmation: ".bux-multi-form__confirmation",
  iconCurrent: "bux-multi-form__step-icon--current",
  iconComplete: "bux-multi-form__step-icon--complete",
  horizontal: ".bux-multi-form__layout--horizontal",
  vertical: ".bux-multi-form__layout--vertical",
};

export const createMultiStepForm = (container: HTMLElement) => {
  const form = container.querySelector<HTMLFormElement>("form") as HTMLFormElement;
  const formContainer = container.querySelector<HTMLElement>(MULTI_STEP_SELECTORS.formContainer);
  const forms = container.querySelectorAll<HTMLElement>(MULTI_STEP_SELECTORS.forms);
  const nextButton = container.querySelector<HTMLButtonElement>(MULTI_STEP_SELECTORS.nextButton);
  const backButton = container.querySelector<HTMLButtonElement>(MULTI_STEP_SELECTORS.backButton);
  const progressBarElement = container.querySelector<HTMLElement>(MULTI_STEP_SELECTORS.progressBar);
  const confirmationContainer = container.querySelector<HTMLElement>(
    MULTI_STEP_SELECTORS.confirmation,
  );

  if (
    !form ||
    !nextButton ||
    !backButton ||
    !progressBarElement ||
    !formContainer ||
    !confirmationContainer ||
    forms.length === 0
  ) {
    throw new TypeError(`Buckeye UX Multi-Step Form is missing required elements.`);
  }

  let currentStep = 0;
  let isProcessing = false;
  const progressBar = createProgressBar(progressBarElement);
  form.setAttribute("novalidate", "true");

  const isFormStart = () => currentStep === 0;
  const isSubmitStep = () => currentStep === forms.length - 1;
  const isConfirmationStep = () => currentStep === forms.length;

  const getStepLabel = (forms: NodeListOf<HTMLElement>, index: number): string => {
    if (!forms[index]) return "";
    return forms[index].querySelector(MULTI_STEP_SELECTORS.stepTitle)?.textContent || "";
  };

  const focusFirstInput = () => {
    const firstInput = forms[currentStep].querySelector<HTMLElement>("input, select, textarea");
    if (firstInput) {
      firstInput.focus({ preventScroll: true });
    }
  };

  const switchActiveStep = (current: HTMLElement, next: HTMLElement) => {
    current.classList.remove("is-active");
    next.classList.add("is-active");
  };

  const updateFormHeight = () => {
    requestAnimationFrame(() => {
      let activeContentHeight = 0;

      if (isConfirmationStep() && confirmationContainer) {
        activeContentHeight = confirmationContainer.offsetHeight;
      } else {
        const activeStepElement = forms[currentStep];
        if (activeStepElement) {
          activeContentHeight = activeStepElement.offsetHeight;
        }
      }

      const buttonContainer = container.querySelector<HTMLElement>(
        MULTI_STEP_SELECTORS.buttonContainer,
      );
      const buttonHeight = buttonContainer ? buttonContainer.offsetHeight : 0;
      const totalHeight = activeContentHeight + buttonHeight;

      form.style.height = `${totalHeight}px`;
    });
  };

  const updateBackButton = () => {
    if (isFormStart() || isProcessing || isConfirmationStep()) {
      backButton.style.display = "none";
      return;
    }
    backButton.style.display = "block";
    const backButtonLabel = getStepLabel(forms, currentStep - 1);
    backButton.textContent = `Back: ${backButtonLabel}`;
  };

  const updateNextButton = () => {
    if (isProcessing) {
      nextButton.textContent = "Processing...";
      nextButton.disabled = true;
      nextButton.classList.add("bux-button--disabled");
      return;
    }

    nextButton.classList.remove("bux-button--disabled");
    nextButton.type = "button";
    delete nextButton.dataset.step;

    if (isSubmitStep()) {
      nextButton.textContent = "Submit";
      nextButton.type = "submit";
      nextButton.dataset.step = "submit";
      return;
    }

    if (isConfirmationStep()) {
      nextButton.textContent = "Complete";
      nextButton.type = "reset";
      nextButton.dataset.step = "complete";
      nextButton.disabled = false;
      return;
    }

    const nextButtonLabel = getStepLabel(forms, currentStep + 1);
    nextButton.textContent = `Next: ${nextButtonLabel}`;
  };

  const updateButtons = () => {
    updateBackButton();
    updateNextButton();
  };

  const validateFormStepInputs = (step: number): boolean => {
    const inputs = forms[step].querySelectorAll<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >("input, select, textarea");

    for (const input of inputs) {
      if (!input.checkValidity()) {
        input.reportValidity();
        return false;
      }
    }
    return true;
  };

  const sendFormData = () => {
    const formData = new FormData(form);

    progressBar.complete();
    isProcessing = true;
    updateButtons();

    const submitEvent = new CustomEvent("bux-form:submit", {
      bubbles: true,
      cancelable: true,
      detail: {
        formData: formData,
        success: () => {
          isProcessing = false;
          currentStep++;
          updateButtons();
          showConfirmationView();
        },
        fail: (errorMessage = "Error") => {
          isProcessing = false;
          alert(errorMessage);
          updateButtons();
        },
      },
    });

    const eventWasNotCancelled = container.dispatchEvent(submitEvent);

    if (eventWasNotCancelled) {
      setTimeout(() => {
        const { success } = submitEvent.detail;
        success();
      }, 1000);
    }
  };

  const updateActiveFormStep = (direction: 1 | -1) => {
    const current = forms[currentStep];
    const next = forms[currentStep + direction];

    if (!current || !next) return;

    const currentHeight = current.offsetHeight;
    const nextHeight = next.offsetHeight;

    if (currentHeight >= nextHeight) {
      switchActiveStep(current, next);
    } else {
      setTimeout(() => switchActiveStep(current, next), 200);
    }
  };

  const handlePageTransition = () => {
    setTimeout(() => {
      container.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);

    const activeStep = forms[currentStep];
    if (!activeStep) return;

    focusFirstInput();
  };

  const showConfirmationView = () => {
    for (const formElement of forms) {
      formElement.style.display = "none";
    }

    if (confirmationContainer) {
      confirmationContainer.style.display = "block";
      updateFormHeight();
      container.scrollIntoView({ behavior: "smooth", block: "start" });
      const confirmationHeader = confirmationContainer.querySelector<HTMLElement>("h3");
      if (confirmationHeader) {
        confirmationHeader.setAttribute("tabindex", "-1");
        confirmationHeader.focus({ preventScroll: true });
      }
    }
  };

  const reset = () => {
    form.reset();
    progressBar.reset();
    currentStep = 0;
    isProcessing = false;

    if (confirmationContainer) confirmationContainer.style.display = "none";

    for (const [index, step] of forms.entries()) {
      step.style.display = "";
      if (index === 0) {
        step.classList.add("is-active");
        focusFirstInput();
      } else {
        step.classList.remove("is-active");
      }
    }

    updateButtons();
    updateFormHeight();
    progressBar.set(currentStep);
  };

  const controlForm = (direction: 1 | -1) => {
    if (direction === 1 && isConfirmationStep()) {
      reset();
      return;
    }

    if (direction === 1 && !validateFormStepInputs(currentStep)) {
      return;
    }

    if (direction === 1 && isSubmitStep()) {
      sendFormData();
      return;
    }

    if (
      (direction === 1 && !isSubmitStep() && !isConfirmationStep()) ||
      (direction === -1 && !isFormStart())
    ) {
      const nextIndex = currentStep + direction;
      updateActiveFormStep(direction);
      updateFormHeight();

      currentStep = nextIndex;
      progressBar.set(currentStep);

      updateButtons();
      handlePageTransition();
    }
  };

  const handleContainerClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (target.closest(MULTI_STEP_SELECTORS.nextButton)) {
      event.preventDefault();
      controlForm(1);
      return;
    }

    if (target.closest(MULTI_STEP_SELECTORS.backButton)) {
      event.preventDefault();
      controlForm(-1);
      return;
    }
  };

  container.addEventListener("click", handleContainerClick);
  backButton.style.display = "none";

  const observer = new IntersectionObserver(
    (entries, observerInstance) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observerInstance.disconnect();
          setTimeout(() => {
            progressBar.set(currentStep);
          }, 300);
        }
      }
    },
    { threshold: 0.2 },
  );

  observer.observe(container);
  updateFormHeight();

  return {
    reset,
  };
};

export const init = () => {
  const multiFormDivs = document.querySelectorAll(MULTI_STEP_SELECTORS.container);

  for (const formDiv of multiFormDivs) {
    const formId = formDiv.id;
    if (formId) {
      const container = document.querySelector<HTMLElement>(`#${formId}`);
      if (!container) continue;

      createMultiStepForm(container);
    }
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
